import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  card: {
    marginTop: theme.spacing(2),
  },
  orgChip: {
    margin: theme.spacing(0.5),
  },
  orgContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

interface GitHubOrg {
  login: string;
  description?: string;
  avatar_url?: string;
}

export const GitHubProfileCard = () => {
  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);
  const [organizations, setOrganizations] = useState<GitHubOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        // Get the access token
        const token = await githubAuth.getAccessToken(['read:org']);
        
        // Fetch organizations from GitHub API
        const response = await fetch('https://api.github.com/user/orgs', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept': 'application/vnd.github+json',
            'User-Agent': 'Backstage',
          },
        });

        if (!response.ok) {
          throw new Error(`GitHub API error: ${response.status} ${response.statusText}`);
        }

        const orgs = await response.json();
        setOrganizations(orgs);
      } catch (err) {
        console.error('Failed to fetch GitHub organizations:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch organizations');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [githubAuth]);

  if (loading) {
    return (
      <Card className={classes.card}>
        <CardHeader title="GitHub Organizations" />
        <CardContent>
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={classes.card}>
        <CardHeader title="GitHub Organizations" />
        <CardContent>
          <Alert severity="warning">
            Unable to load GitHub organizations: {error}
          </Alert>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            This might be due to missing permissions or scope issues.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardHeader 
        title="GitHub Organizations" 
        subheader={`Member of ${organizations.length} organization${organizations.length !== 1 ? 's' : ''}`}
      />
      <CardContent>
        {organizations.length === 0 ? (
          <Typography color="textSecondary">
            No organizations found or you may not have public membership in any organizations.
          </Typography>
        ) : (
          <Box className={classes.orgContainer}>
            {organizations.map(org => (
              <Chip
                key={org.login}
                label={org.login}
                className={classes.orgChip}
                variant="outlined"
                color="primary"
                clickable
                component="a"
                href={`https://github.com/${org.login}`}
                target="_blank"
                rel="noopener noreferrer"
              />
            ))}
          </Box>
        )}
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 16, display: 'block' }}>
          This shows organizations where you have public membership. Private memberships may not be visible.
        </Typography>
      </CardContent>
    </Card>
  );
};